import {
  BinocularsIcon,
  CaretDownIcon,
  ChartBarIcon,
  ChartLineUpIcon,
  ChatCircleDotsIcon,
  CheckIcon,
  DotsThreeIcon,
  FileTextIcon,
  FlowArrowIcon,
  GearIcon,
  HandsClappingIcon,
  HandshakeIcon,
  ImageSquareIcon,
  MegaphoneIcon,
  NotePencilIcon,
  PackageIcon,
  PaletteIcon,
  PaperPlaneTiltIcon,
  type Icon as PhosphorIcon,
  PlugIcon,
  PlusIcon,
  QuestionIcon,
  SignOutIcon,
  SquaresFourIcon,
  SwatchesIcon,
} from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ChatSessionMenu } from '@/components/chat/ChatSessionMenu';
import { SentryFeedbackTrigger } from '@/components/feedback/SentryFeedbackTrigger';
import { OverflowFadeText } from '@/components/layout/OverflowFadeText';
import { SharedAccountHint } from '@/components/layout/SharedAccountHint';
import { UserMenuHeader } from '@/components/layout/UserMenuHeader';
import { AddBrandDialog } from '@/components/settings/AddBrandDialog';
import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import { DraftLibrary } from '@/components/sidebar/DraftLibrary';
import { SidebarSectionHeader } from '@/components/sidebar/SidebarSectionHeader';
import { useSidebarViewportCollapse } from '@/components/sidebar/useSidebarViewportCollapse';
import { Avatar } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EmptyState } from '@/components/ui/empty';
import { LogoMark } from '@/components/ui/logo';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { WorkspaceGlyph } from '@/components/workspace/WorkspaceGlyph';
import { type ThemeMode, useTheme } from '@/contexts/ThemeContext';
import { usePreviewAccount } from '@/hooks/account/usePreviewAccount';
import { useInfiniteRecentChatSessions } from '@/hooks/chat/useInfiniteRecentChatSessions';
import { useAuth } from '@/hooks/use-auth';
import { APPEARANCE_OPTIONS, useAppearanceToggle } from '@/hooks/useAppearanceToggle';
import { BRAND_KIT_CAP, useBrandKits } from '@/hooks/useBrandKits';
import { useConnectionAttentionCount } from '@/hooks/useConnectionAttentionCount';
import { useCreditWallet } from '@/hooks/useCreditWallet';
import { useCurrentContext } from '@/hooks/useCurrentContext';
import { useManualPostDraft } from '@/hooks/useManualPostDraft';
import { useTierState } from '@/hooks/useTier';
import { useStartBrandResearcher } from '@/lib/agent-links/startBrandResearcher';
import { ENGAGEMENT_NAV_VISIBLE, isTeamInvitesEnabled } from '@/lib/feature-flags';
import { getDisplayName } from '@/lib/format-name';
import { isStoreAccount } from '@/lib/identity/storeAccount';
import { anotherDialogIsOpen, isTypingTarget } from '@/lib/keyboard';
import { useProductsVisibility } from '@/lib/products/productsVisibility';
import { useReachEntitlement } from '@/lib/reach/entitlement';
import { routes } from '@/lib/routes';
import { SIDEBAR_POPOVER_ICON, SIDEBAR_POPOVER_ITEM, SIDEBAR_POPOVER_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';
import { openDock } from '@/stores/_panel-mutex';
import { useRightDockStore } from '@/stores/useRightDockStore';
import { logger } from '@/utils/logger';

// Sibling-surface parity with DraftLibrary's "Show more" cap — a default
// page's worth of chat titles read as a wall of text; collapse to a scan-able
// handful and let "Show more" (then scroll) reveal the rest.
const RECENT_CHATS_COLLAPSED = 5;

// Home names a PLACE, the way every sibling row does. It shipped as "New Chat"
// — an action label — until 2026-08-07, which hid what the page actually is
// (composer + KPIs + Daily Pulse) and made the row the only way to start a
// thread. Starting a thread now belongs to the + on the Chat section, and to
// ⌘0; this row just navigates.
const mainNavItems = [{ label: 'Home', icon: SquaresFourIcon, href: '/' }];

// Popover-row contract — mirrors `PromptSettingsMenu` row treatment so the
// user-menu, prompt-settings, and platform-plug menus all read as one system.
//   - rounded-sm  (8px) — same as PromptSettingsMenu rows
//   - px-2 py-1.5 — tighter rhythm than the default DropdownMenuItem
//   - icon size-3.5 + weight="regular" + text-muted-foreground/70
//   - label text-caption font-normal text-foreground/85
// Match `PromptSettingsMenu` exactly — same icon weight/color and label
// treatment so the user-menu, prompt-settings, connected-accounts, and
// platform-plug popovers all read as one system. Bumped icon from
// /70 → /85-via-default-muted-foreground for stronger contrast on dark mode
// and to align with Connected Accounts (icons render at full muted-fg).
// Mobile is bumped via SIDEBAR_POPOVER_* (ui-tokens.ts) to match the sidebar
// row's own mobile scale (16px text / 24px icon) — desktop stays exactly as
// documented above via the appended md: classes.
const USER_MENU_ITEM = cn(
  SIDEBAR_POPOVER_ITEM,
  'flex items-center rounded-sm cursor-pointer focus:bg-muted/50 data-[highlighted]:bg-muted/50',
);
const USER_MENU_ICON = cn(SIDEBAR_POPOVER_ICON, 'text-muted-foreground');
const SETTINGS_SHORTCUT_HINT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
    ? '⌘;'
    : 'Ctrl+;';
const USER_MENU_LABEL = cn(SIDEBAR_POPOVER_LABEL, 'md:text-caption');
const WORKSPACE_MENU_LABEL =
  'text-sidebar-nav-mobile md:text-sidebar-nav-desktop font-normal text-foreground/85';

// ChatGPT-style recent-chat title. The shadcn sidebar primitive force-reserves
// `pr-8` on ANY row that has a SidebarMenuAction — permanently, not on hover —
// which is why titles truncated short at rest. We neutralize that back to the
// normal `pr-2` so the title flows full-width, then re-reserve `pr-8` only on
// row-hover / open-menu so the title clips *more* (shows less) to make room for
// the ⋯ exactly when it appears. The base `transition-[…,padding]` animates it.
const RECENT_TITLE_ROW =
  // No unprefixed height/text-size here (see SIDEBAR_ROW_HEIGHT comment above)
  // — mobile inherits the primitive's own 44px row / 16px text defaults.
  // Quieter than a nav row on purpose, and quieter again since 2026-08-12
  // (/75 → /55). A conversation title is RECALL material — you scan it only
  // when looking for a thread you already know exists — so at nav-row weight a
  // list of them reads as a wall of live links competing with the destinations
  // above. ChatGPT and marketer.com both hold this list well below their nav.
  // Hover still returns it to full strength, so nothing is lost.
  // ⚠️ The `dark:` half is LOAD-BEARING. `sidebarMenuButtonVariants` sets
  // `dark:text-sidebar-foreground/85`, and a `dark:`-prefixed utility beats an
  // unprefixed one regardless of source order — so a bare `text-foreground/55`
  // is overridden in dark mode and the row renders at the NAV colour.
  // Measured 2026-08-12: nav and this row both computed
  // `oklab(0.71181 0.00361 -0.01242 / 0.85)`, byte-identical. The quieting
  // existed only in light mode and was a silent no-op in dark. Any future
  // colour change here must set BOTH halves.
  'md:h-7 md:text-sidebar-nav-desktop text-foreground/55 dark:text-sidebar-foreground/55 hover:text-foreground dark:hover:text-sidebar-foreground ' +
  // Desktop rest: neutralize the primitive's always-on pr-8 → full-width title.
  'group-has-data-[sidebar=menu-action]/menu-item:pr-2 ' +
  // Mobile/touch: the ⋯ is always visible (no hover), so always reserve room.
  'max-md:pr-8! ' +
  // Desktop: reserve (clip the title more) only on hover/open. Tailwind v4
  // suffix-important so it wins over the equal-specificity pr-2 above.
  'md:group-hover/menu-item:pr-8! md:group-has-[[data-state=open]]/menu-item:pr-8! ' +
  'group-has-[[data-state=open]]/menu-item:bg-sidebar-accent group-has-[[data-state=open]]/menu-item:text-foreground';

// ChatGPT/Claude density: 28px rows on desktop, matching the height
// SidebarMenuSubButton already uses for nested items (h-7) — reusing an
// already-established value instead of inventing a new one. Deliberately
// desktop-only: mobile inherits sidebarMenuButtonVariants' own
// h-sidebar-row-mobile (44px) + text-sidebar-nav-mobile (16px) defaults.
// A prior version of this constant also hardcoded `h-9 text-sm` for mobile —
// that flattened mobile rows to the same cramped size as desktop, which is
// the root cause of the 2026-07-04 "icons/text too small on mobile" report.
//
// 2026-08-07 — this row briefly ran at `md:text-caption md:[&>svg]:size-3.5`,
// borrowing the POPOVER contract. That was two steps down, not one, and the
// sidebar came back illegible ("text and icons are way too tiny", founder, same
// day). Popover rows can be quieter than nav rows: a popover is already the
// focus of attention when it is open, while the sidebar is read at a glance
// from the periphery all day. So the label and icon return to the primitive's
// own desktop scale (`md:text-sm`, `md:[&>svg]:size-4`) and only the HEIGHT
// stays tightened — 28px rows give ChatGPT/Claude pitch without shrinking
// anything you actually have to read. Mobile scale stays owned by the
// primitive and by SIDEBAR_POPOVER_* in ui-tokens, never hardcoded here.
// 2026-08-12: 28px → 32px on desktop. Founder: the rows "feel cramped" next to
// marketer.com's, whose nav breathes noticeably more.
//
// ⚠️ AIR WAS ADDED, TYPE WAS NOT SHRUNK, and that split is deliberate. The
// request also said the text and icons look "slightly too big" — but the only
// step down available in our tokens is exactly the one that shipped on
// 2026-08-07 (`md:text-caption` 12px + `md:[&>svg]:size-3.5`) and was reverted
// the same day: "text and icons are way too tiny". There is no half-step
// between 14px and 12px, or between 16px and 14px, in the token scale.
//
// So the cramping is relieved with SPACE rather than by shrinking glyphs the
// user has to read from the periphery all day. If a size step is still wanted
// after seeing this, it needs new tokens (13px / 15px), not a repeat of the
// reverted change.
// 2026-08-12, second adjustment the same day, and the pair only makes sense
// read together: rows went 28px → 32px when the label was still 14px and the
// glyphs 20px, because the complaint then was "cramped". Once the label dropped
// to 13px and both icon registers to 16px, that same 32px box became too much
// air — founder: "each item has too much top/bottom padding, the rows are too
// tall". Same box, opposite verdict, because the contents shrank underneath it.
//
// Back to 28px, which is now genuinely tighter than where the day started: the
// row is the height it was, holding smaller type.
const SIDEBAR_ROW_HEIGHT = 'md:h-7';

// Quiet, non-interactive announcement for newly shipped destinations. Keep it
// inline with the label, like the reference, and hide it in the collapsed rail
// where there is no label for it to qualify.
//
// `bg-sidebar` pins the pill to the row's RESTING background, not
// `transparent` — the bug the reference caught. A transparent pill inherits
// whatever sits behind it, and the row's hover/active state is
// `bg-sidebar-accent`, which measures IDENTICAL to `--border` in dark mode
// (both `240 3.7% 15.9%`) and one point off it in light mode (`89.4%` vs
// `90%`) — so the instant a row is hovered or active, the pill's own border
// sits on a background the same color as itself and disappears. Pinning the
// background stops the pill from ever compositing against the accent color:
// at rest it blends into the row (by design, same as the reference), and on
// hover/active it reads as a solid chip floating over the darker/lighter
// accent fill, border intact — exactly the reference's behavior. Also
// `border-border` (not `/90` at `0.5px`) for a line that reads as a line
// rather than a soft edge.
// Nav lists tighten from the primitive's `gap-1` to `gap-0.5`: 28px row + 2px
// gap = a 30px pitch, which is what the reference sidebar measures. Applied at
// the call sites rather than in `sidebar.tsx` because the Chat and Post-drafts
// lists share that primitive and keep the looser rhythm.
const SIDEBAR_NAV_MENU = 'gap-0.5';

// `SIDEBAR_SUB_LIST` and `SIDEBAR_SUB_ROW` lived here until 2026-08-12. They
// styled the sub-navigation rail under Creatives — the only nesting in the app —
// and were deleted with it. Nothing in this sidebar nests any more, so a nested
// row has no styling to inherit; that is deliberate, not an omission. If a
// future section genuinely needs children, argue the case in
// `docs/planning/SIDEBAR-IA-2026-08-12.md` first — the default answer is a
// destination in `⌘K` (`src/lib/navigation/destinations.ts`), not a sub-row.

// A section nests when its children are different KINDS of object; it keeps
// in-page tabs when its children are one object in different states. That is
// the rule that makes the asymmetry below non-arbitrary:
//
//   • Creatives → Briefs (a plan) · Studio (a generator) · Assets (a file) —
//     three different objects, so three sub-rows.
//   • Insights → Overview · Audience · Content · Competitors — one analytics
//     surface in four views, so the page owns the tabs and the sidebar stays flat.
//   • Publishing → Scheduled · Posts · Feed — one object (a post) in three
//     states, so it stays flat and its page header owns the tabs.
//
// Sub-rows render only while the user is inside the section. A permanently
// expanded block would put the flat eight-destination sidebar this pass exists
// to shrink straight back on screen.
//
// Briefs is first because the parent row's own target is `/creatives/briefs` —
// the first sub-row must equal the parent's index or the parent link silently
// lands on row 2.
//
// 2026-08-10 — Discover left this list for a top-level row (see the Core
// group below): it stopped being top-level on 2026-08-07 on the theory that
// research belongs inside the section it feeds, but with Studio/Assets/Ad
// copy also live, five heterogeneous children in one flyout read as clutter,
// not depth — direct founder call to reverse the nesting.
//
// Ad copy went through THREE states in one session: standalone sub-row →
// briefly dropped with no nav entry at all (broke `CreativesPageHeader`'s own
// "icon ties to a sidebar row" contract) → restored as a 4th sub-row → and
// finally removed for real, on direct founder instruction, in favor of the
// thing marketer.com actually does: ad copy is written INSIDE a Brief
// Concept, not on a separate page (`docs/research/competitors/marketer-com/
// CATALOG.md` §4 — their Creatives nav is Briefs/Studio/Assets only, no
// ad-copy destination). The real entry point now lives on the Concept page
// itself — see `ConceptCopyOutput` (`src/components/briefs/concept/
// ConceptCopyOutput.tsx`), mounted on `BriefConceptPage.tsx`. `/creatives/
// ad-copy` no longer exists; it redirects to `/creatives/briefs`.
// The four objects you make things with. FLAT — no parent row, no caret, no
// disclosure state.
//
// Until 2026-08-12 these lived as sub-rows under a `Creatives` parent whose own
// target was `/creatives/briefs`. The parent existed only to hold children, and
// it was the ONLY nesting in the app: Campaigns has real sub-surfaces (drafts,
// launches) and Workflows has templates, and neither exposes them. One nested
// row out of three eligible ones reads as arbitrary, because it is. It also
// carried the sidebar's third distinct disclosure behaviour and a localStorage
// key to remember it.
//
// Founder, 2026-08-12: "I don't want anything nested under creatives."
// marketer.com reached the same conclusion independently — their 2026-08-08
// capture nests Briefs under Creatives, their 2026-08-12 one has four flat
// Tools peers and no Studio row at all. See
// `docs/research/competitors/marketer-com/OBSERVED-2026-08-12-sidebar-and-brief-cards.md`.
//
// Routes stayed nested (`/creatives/*`) for three more days after the nav
// flattened, which left the URL implying a hierarchy the sidebar no longer
// had — founder, 2026-08-15: "why does it say creatives before it, because
// we don't have that path anymore either." Flattened to `/briefs`, `/studio`,
// `/assets` (this file's `href`s above) the same day; the old `/creatives/*`
// redirects were removed outright 2026-08-23 (dead routes, pre-launch).
const CREATE_NAV_ITEMS = [
  {
    label: 'Campaigns',
    href: '/campaigns',
    icon: MegaphoneIcon,
    match: ['/campaigns'],
  },
  {
    label: 'Briefs',
    href: '/briefs',
    icon: FileTextIcon,
    match: ['/briefs', '/ad-copy'],
  },
  {
    label: 'Studio',
    href: '/studio',
    icon: ImageSquareIcon,
    match: ['/studio'],
  },
  // Assets is NOT here (corrected 2026-08-15 — the previous comment cited a
  // stale capture; see below). On marketer.com the creative library IS
  // `/dashboard/creatives` — but the earlier `Briefs · Studio · Assets`
  // sub-nav description (`CATALOG.md` §4a, from the 2026-08-08 capture) is
  // itself superseded by `OBSERVED-2026-08-12-sidebar-and-brief-cards.md`:
  // the live nav has NO Studio row and NO Assets row at all — Tools is four
  // FLAT peers (Campaigns, Creatives, Briefs, Workflow), no sub-nav strip
  // under any of them. So there is no reference-product sub-nav to defer to
  // here; the actual reasoning is (a) no surveyed competitor — marketer.com,
  // Sprout, Metricool, SOCi, Hootsuite, Later, Buffer — ships a standalone
  // Assets/Library nav destination, and (b) a media library is a thing you
  // reach from the work that consumes it, not a destination you visit for
  // its own sake. The old comment also said Assets sits "behind Studio" —
  // that was ASPIRATIONAL when written (no such link existed yet) and is
  // only true now: `StudioPage.tsx` has a labelled "View assets" page-header
  // action to `/creatives/assets` (2026-08-15). Reachable today via `⌘K`
  // (`destinations.ts`) and that Studio-page action — deliberately still no
  // sidebar row of its own; the flat nav and the Assets exclusion both stand.
  {
    label: 'Workflows',
    href: '/workflows',
    icon: FlowArrowIcon,
    match: ['/workflows'],
  },
] as const satisfies ReadonlyArray<{
  label: string;
  href: string;
  icon: PhosphorIcon;
  match: readonly string[];
}>;

function InsightsNavGroup({ onNavigate }: { onNavigate: () => void }) {
  const location = useLocation();
  const active = location.pathname.startsWith('/insights');

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={active}
        tooltip="Insights"
        className={SIDEBAR_ROW_HEIGHT}
      >
        {/* A hardcoded `to="/insights"` re-navigates to the bare index on
              every click, which even a search-preserving redirect can't recover
              from — the query string is already gone by the time it fires.
              While already inside Insights, target the current path + search
              unchanged instead of resetting the user's tab/filter (beta
              scorecard P1 #6). */}
        <Link
          to={
            active ? { pathname: location.pathname, search: location.search } : '/insights/overview'
          }
          onClick={onNavigate}
        >
          <ChartBarIcon weight="regular" />
          <span>Insights</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

const RECENT_CHATS_OPEN_STORAGE_KEY = 'mora:sidebar:recent-chats-open:v2';
const POST_DRAFTS_OPEN_STORAGE_KEY = 'mora:sidebar:post-drafts-open:v1';

export function AppSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  // Brand setup runs in the HOME composer, not the right dock — founder
  // 2026-08-22. See startBrandResearcher.ts for why.
  const startBrandResearcherAgent = useStartBrandResearcher();
  const { user, signOut } = useAuth();
  // Reach is visible only in entitled internal workspaces.
  const { enabled: reachEnabled } = useReachEntitlement();
  const { visible: productsVisible } = useProductsVisibility();
  // Header identity switcher — reads the SAME `useBrandKits` primitive the
  // composer's BrandKitChip and Settings → Brand use.
  //
  // ⚠️ CORRECTED 2026-08-22, twice over. This comment used to say switching
  // here "writes `is_default` in the DB like every other caller". It did, and
  // that WAS the bug: `brand_kits` is account-scoped, so one teammate
  // switching moved every member of the account. Switching now writes this
  // user's own pointer (`user_profiles.active_brand_kit_id`) via
  // `switchWorkspace()` in `src/lib/workspace/`; `is_default` survives as the
  // ACCOUNT's default, inherited by anyone who has never switched.
  //
  // The same comment also claimed `user_connections` "has no `brand_kit_id`".
  // It has had one in production since migration 20260820230216. The column is
  // still 100% NULL and nothing reads it yet, and NULL there means
  // ACCOUNT-WIDE rather than "unassigned" (see
  // src/lib/connections/resolveBrandVisibleConnections.ts on the connections
  // branch), so the rendering below is still correct today: connected accounts
  // are one account-wide list, not nested per brand kit. It is correct for a
  // different reason than the one previously written here.
  //
  // See docs/superpowers/plans/2026-08-22-workspace-switching-spine.md, and
  // docs/planning/HANDOFF-MARKETER-PARITY-2026-08-09.md for the marketer.com/Jam
  // reference this header was built against.
  const {
    selectableKits: brandKits,
    activeKit,
    setActiveKit,
    isLoading: brandKitsLoading,
    isActiveKitPending,
  } = useBrandKits();
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const [addBrandOpen, setAddBrandOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const { tier } = useTierState();
  const atBrandKitCap = brandKits.length >= BRAND_KIT_CAP;
  const { themeMode, setAppearance, options: appearanceOptions } = useAppearanceToggle();
  // Falls back to the module CONSTANT, not to `appearanceOptions[0]`. The hook
  // always returns the full three-item tuple in production, but a test that
  // mocks the hook can hand back an empty array — and `appearanceOptions[0]`
  // then THROWS during render, taking the entire sidebar down. It did: this
  // line shipped in b65e91da4 and broke all 14 `app-sidebar.nav` tests. A
  // fallback whose whole job is to guarantee a value must not itself be able
  // to be empty; `APPEARANCE_OPTIONS` is an `as const` tuple, so `[0]` is
  // provably there.
  const activeAppearanceLabel =
    appearanceOptions.find((option) => option.value === themeMode)?.label ??
    APPEARANCE_OPTIONS[0].label;
  const wallet = useCreditWallet();
  const { isLoading: isWalletLoading, isError: isWalletError } = wallet;
  const { isMobile, setOpenMobile, toggleSidebar } = useSidebar();
  useSidebarViewportCollapse();
  const engagementOpen = useRightDockStore((state) => state.activePanel === 'engagement');
  const { openSettings } = useSettingsDialog();
  // Workspaces belong to the account owner; a teammate switches between the ones
  // they were granted and cannot create, manage or set up new ones.
  const isTeammate = useCurrentContext().role === 'member';
  const { isPreviewAccount } = usePreviewAccount();
  const [menuOpen, setMenuOpen] = useState(false);
  const connectionsToFix = useConnectionAttentionCount(menuOpen);
  const [recentChatsExpanded, setRecentChatsExpanded] = useState(false);
  const [postDraftsOpen, setPostDraftsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(POST_DRAFTS_OPEN_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(POST_DRAFTS_OPEN_STORAGE_KEY, String(postDraftsOpen));
    } catch {
      return;
    }
  }, [postDraftsOpen]);
  const [recentChatsOpen, setRecentChatsOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      return window.localStorage.getItem(RECENT_CHATS_OPEN_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(RECENT_CHATS_OPEN_STORAGE_KEY, String(recentChatsOpen));
    } catch {
      return;
    }
  }, [recentChatsOpen]);
  const { createManualPostDraft, isCreatingManualPost } = useManualPostDraft();

  const {
    sessions: recentSessions,
    isLoading: isLoadingRecentSessions,
    hasNextPage: hasMoreSessions,
    fetchNextPage: fetchMoreSessions,
    isFetchingNextPage: isFetchingMoreSessions,
  } = useInfiniteRecentChatSessions({ pageSize: 20 });

  // Scroll-triggered pagination for the Recent-chats rail — same
  // IntersectionObserver-on-a-sentinel convention as LibraryPage's list/grid.
  const recentSessionsSentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = recentSessionsSentinelRef.current;
    if (!el || !hasMoreSessions) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchMoreSessions();
      },
      { rootMargin: '160px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
    // `recentChatsExpanded` isn't read in the body, but the sentinel <div>
    // only mounts once expanded — re-run so the observer attaches to it.
  }, [hasMoreSessions, fetchMoreSessions, recentChatsExpanded]);

  const avatarUrl = user?.user_metadata?.avatar_url as string | undefined;

  const isActiveRoute = (href: string): boolean => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/auth');
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  };

  const triggerNewChat = () => {
    if (location.pathname !== '/') navigate('/');
    window.dispatchEvent(new CustomEvent('createPage:newChat'));
  };

  const closeMobileSidebar = (): void => {
    if (isMobile) setOpenMobile(false);
  };

  // Global keybinds — both shortcuts work on Mac (⌘) and PC (Ctrl) via the
  // shared `metaKey || ctrlKey` modifier. Updated 2026-05-01:
  //   - New Chat:  ⌘0 / Ctrl+0  (was ⇧⌘O — too cramped, conflicted with
  //                              browser Reopen-Closed-Tab muscle memory)
  //   - Settings:  ⌘; / Ctrl+;  (was ⌘, — comma after the symbol read as
  //                              an editorial pause, not a key)
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const mod = event.metaKey || event.ctrlKey;
      if (!mod) return;
      if (anotherDialogIsOpen()) return;

      // New Chat: ⌘0 / Ctrl+0 — no shift required.
      const isNewChat =
        !event.shiftKey &&
        !event.altKey &&
        (event.key === '0' || event.code === 'Digit0' || event.code === 'Numpad0');
      // Settings: ⌘; / Ctrl+; — semicolon, no shift.
      const isSettings =
        !event.shiftKey && !event.altKey && (event.key === ';' || event.code === 'Semicolon');

      if (isNewChat) {
        if (isTypingTarget(event.target)) return;
        event.preventDefault();
        triggerNewChat();
        return;
      }
      if (isSettings) {
        event.preventDefault();
        openSettings('profile');
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, openSettings]);

  const creditsAvailable = wallet.data?.creditsAvailable;
  const sidebarCreditsLabel = isWalletLoading
    ? 'Credits left are loading'
    : isWalletError || !wallet.data
      ? 'Credits left are unavailable'
      : `${creditsAvailable.toLocaleString()} credits left`;
  const sidebarDisplayName = getDisplayName(user, isStoreAccount(user) ? 'Store account' : 'User');
  const hasBrandKits = brandKits.length > 0;
  // Label falls back to the bare wordmark for a brand-new account with no kit
  // yet — never a placeholder like "Set up" sitting where a name normally
  // reads, which would look broken rather than merely empty. Also falls back
  // while isActiveKitPending: until the per-user workspace pointer resolves,
  // `activeKit` may be the ACCOUNT default rather than this user's real one,
  // and naming it here would flash the wrong workspace in the header for a
  // moment. See useBrandKits.ts's isActiveKitPending doc.
  const brandSwitcherLabel =
    hasBrandKits && !isActiveKitPending ? (activeKit?.name ?? 'mora') : 'mora';
  const orderedBrandKits = activeKit
    ? [activeKit, ...brandKits.filter((kit) => kit.id !== activeKit.id)]
    : brandKits;
  const workspaceLogoUrl = (kit: (typeof brandKits)[number]) =>
    isDarkMode
      ? (kit.logo_light_url ?? kit.logo_url ?? kit.logo_dark_url)
      : (kit.logo_dark_url ?? kit.logo_url ?? kit.logo_light_url);

  const handleBrandMenuOpenChange = (open: boolean) => {
    setBrandMenuOpen(open);
  };

  return (
    <Sidebar collapsible="icon" className="border-none">
      <SidebarHeader className="pb-1">
        <SidebarMenu>
          <SidebarMenuItem>
            {/* Header identity row. Search (⌘K) used to live here as a visible
                icon — removed 2026-08-18 to match the reference apps (Jam,
                marketer.com, shadcn's own sidebar-07), none of which surface a
                search affordance in the header. The shortcut itself is
                unaffected: GlobalSearchDialog owns its own document-level
                keydown listener, independent of this button. */}
            <div className="group/workspace-header flex h-8 w-fit max-w-full items-center rounded-lg px-1 group-data-[collapsible=icon]:hidden hover:bg-sidebar-accent focus-within:bg-sidebar-accent">
              <button
                type="button"
                className="flex size-6 shrink-0 items-center justify-center rounded-sm text-foreground transition-opacity hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={toggleSidebar}
                aria-label="Collapse sidebar · ⌘B"
                title="Collapse sidebar · ⌘B"
              >
                <LogoMark className="!h-3 !w-auto" />
              </button>
              <DropdownMenu open={brandMenuOpen} onOpenChange={handleBrandMenuOpenChange}>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    className="h-7 w-fit! max-w-full px-1 py-1 hover:bg-transparent data-[state=open]:bg-transparent"
                    tooltip="Switch workspace"
                  >
                    {/* Fade, not an ellipsis. `truncate` clipped this to
                        "Cactus …", which reads as a broken name rather than a
                        long one — and the user identity row at the bottom of
                        this same sidebar already fades. `block` is
                        load-bearing; see OverflowFadeText's own note. */}
                    <OverflowFadeText
                      className="block max-w-40 text-sm font-normal text-sidebar-foreground/85"
                      title={brandSwitcherLabel}
                    >
                      {brandSwitcherLabel}
                    </OverflowFadeText>
                    <CaretDownIcon
                      className="ml-1 !size-3 shrink-0 text-muted-foreground/70"
                      weight="fill"
                    />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  side="bottom"
                  align="start"
                  alignOffset={-28}
                  sideOffset={6}
                  className="w-64 max-h-(--radix-dropdown-menu-content-available-height) overflow-x-hidden overflow-y-auto overscroll-contain rounded-xl border border-border/60 bg-popover/95 p-1 shadow-elevation-3 backdrop-blur-xl"
                >
                  {brandKitsLoading || isActiveKitPending ? (
                    <div className="flex flex-col gap-1 p-1">
                      {[0, 1].map((index) => (
                        <div key={index} className="flex items-center gap-2 px-2 py-1.5">
                          <Skeleton className="size-5 rounded-sm" />
                          <Skeleton className="h-3 w-32 rounded-sm" />
                        </div>
                      ))}
                    </div>
                  ) : hasBrandKits ? (
                    <>
                      {orderedBrandKits.map((kit) => {
                        const isActive = kit.id === activeKit?.id;
                        return (
                          <DropdownMenuItem
                            key={kit.id}
                            onClick={() => {
                              setActiveKit(kit.id);
                              handleBrandMenuOpenChange(false);
                            }}
                            className={USER_MENU_ITEM}
                          >
                            {workspaceLogoUrl(kit) ? (
                              <Avatar
                                size="xs"
                                src={workspaceLogoUrl(kit)}
                                alt=""
                                name={kit.name}
                                fallback={<WorkspaceGlyph className="size-2.5" />}
                                showIcon={false}
                                className="size-3.5 rounded-sm ring-1 ring-border/40"
                                fallbackClassName="rounded-sm bg-muted"
                                imageClassName="object-contain p-px"
                              />
                            ) : (
                              <WorkspaceGlyph className={USER_MENU_ICON} />
                            )}
                            <span className={cn(WORKSPACE_MENU_LABEL, 'min-w-0 flex-1 truncate')}>
                              {kit.name}
                            </span>
                            {isActive ? (
                              <CheckIcon
                                className="size-3.5 shrink-0 text-foreground"
                                weight="regular"
                              />
                            ) : null}
                          </DropdownMenuItem>
                        );
                      })}
                      {isTeammate ? null : (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              openSettings('workspaces');
                              handleBrandMenuOpenChange(false);
                            }}
                            className={USER_MENU_ITEM}
                          >
                            <GearIcon className={USER_MENU_ICON} weight="regular" />
                            <span className={WORKSPACE_MENU_LABEL}>Manage workspaces</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={atBrandKitCap}
                            onClick={() => {
                              setAddBrandOpen(true);
                              handleBrandMenuOpenChange(false);
                            }}
                            className={USER_MENU_ITEM}
                          >
                            <PlusIcon className={USER_MENU_ICON} weight="regular" />
                            <span className={WORKSPACE_MENU_LABEL}>
                              {atBrandKitCap
                                ? `${BRAND_KIT_CAP} workspace limit reached`
                                : 'Add new workspace'}
                            </span>
                          </DropdownMenuItem>
                        </>
                      )}
                    </>
                  ) : isTeammate ? (
                    <DropdownMenuItem disabled className={USER_MENU_ITEM}>
                      <WorkspaceGlyph className={USER_MENU_ICON} />
                      <span className={WORKSPACE_MENU_LABEL}>
                        No workspace access yet. Ask the account owner.
                      </span>
                    </DropdownMenuItem>
                  ) : (
                    /* The FIRST workspace goes through the same dialog as every
                       later one (founder 2026-08-22). It used to hand straight
                       to the Brand researcher, which made the chat-only flow
                       the only way a new user could ever get a workspace —
                       and that is the flow the competitor survey singled out:
                       0 of 10 products scan a website to CREATE the unit, the
                       median form is name-only, and Mora's scan is an
                       enrichment layered on top (see
                       docs/superpowers/specs/2026-08-22-workspace-model-and-switcher.md §2).

                       It also fixes a real defect. The agent path creates the
                       kit with `save_brand_kit`, which writes `brand_kits` and
                       NOTHING else — it never writes
                       `user_profiles.active_brand_kit_id`, so it never switches
                       you into what you just made. It only looked correct
                       because `save_brand_kit` sets `is_default` on an
                       account's first kit and the resolver falls back to the
                       account default when no pointer exists. Correct by
                       accident, for exactly one kit. `AddBrandDialog` switches
                       explicitly, then hands the website to the agent.

                       This "Set up your brand" item is the FIRST workspace's
                       entry; the "New workspace" item just below is a SECOND
                       (or later) one, gated only on the real entitlement cap
                       (`atBrandKitCap`) — never on tier. A consumer with one
                       workspace was previously unable to reach this item at
                       all (`canAddBrand = tier !== 'consumer'` hid it), which
                       is the exact bug this fixes. */
                    <DropdownMenuItem
                      onClick={() => {
                        setAddBrandOpen(true);
                        handleBrandMenuOpenChange(false);
                      }}
                      className={USER_MENU_ITEM}
                    >
                      <SwatchesIcon className={USER_MENU_ICON} weight="regular" />
                      <span className={WORKSPACE_MENU_LABEL}>Set up your brand</span>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <SidebarMenuButton
              className="hidden group-data-[collapsible=icon]:flex items-center justify-center"
              onClick={toggleSidebar}
              tooltip="Expand sidebar"
            >
              <LogoMark className="!h-4 !w-auto" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* ChatGPT-style pinned rail: Home never scrolls away, even when
            Projects/Discover/Drafts/Recent-chats overflow below.

            This row NO LONGER starts a chat. It used to dispatch
            `createPage:newChat` on every click — which is why it was labelled
            with an action ("New Chat") rather than a place, and why landing on
            Home from the sidebar silently discarded the thread you were
            reading. Starting a thread is now the + on the Chat section below,
            and ⌘0, both of which still dispatch the same event. */}
        <SidebarMenu className={SIDEBAR_NAV_MENU}>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActiveRoute(mainNavItems[0].href)}
              tooltip="Home"
              className={SIDEBAR_ROW_HEIGHT}
            >
              {/* No hover rotate/scale on the icon: it had no equivalent in the
                  reference, and novelty motion on the top nav row is the exact
                  opposite of a density pass. */}
              <Link to={mainNavItems[0].href} onClick={closeMobileSidebar}>
                <SquaresFourIcon weight="regular" />
                <span className="truncate">{mainNavItems[0].label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Core — deliberately unlabelled, the way the reference leaves its
            primary nav unlabelled. Insights stays a single destination; its
            Overview/Audience/Content/Competitors tabs live inside the page.

            Integrations is NOT a nav row. It shipped here on 2026-08-07 and was
            removed the same day: connecting a channel is a settings act, it
            already lives in the settings dialog, and a permanent top-level row
            for something you touch twice a year is exactly the clutter this
            sidebar is being cut back to fix. It stays at
            Settings → Integrations, which is where its two-stage detail view
            (list → one connected channel, with its sync state and data
            coverage) belongs too. */}
        <SidebarGroup className="pt-0">
          <SidebarMenu className={SIDEBAR_NAV_MENU}>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActiveRoute('/publishing')}
                tooltip="Publishing"
                className={SIDEBAR_ROW_HEIGHT}
              >
                <Link to="/publishing" onClick={closeMobileSidebar}>
                  <PaperPlaneTiltIcon weight="regular" />
                  <span>Publishing</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <InsightsNavGroup onNavigate={closeMobileSidebar} />
            {/* Discover sits in Core, not Tools: it's a "know" surface
                (trending articles, competitor scan, pulse) matching Home/
                Insights/Publishing's own stated rule below, not a "make"
                surface like Campaigns/Creatives/Workflows. It moved back to
                top-level on 2026-08-10 — it had been nested under Creatives
                since 2026-08-07 on the theory that research belongs inside
                the section it consumes it, but with Studio/Assets/Ad copy
                also live there, five heterogeneous children in one flyout
                read as clutter, not depth. A first pass at this same move put
                it in Tools instead, which repeats the taxonomy mistake this
                comment exists to prevent — a fresh-context design critique
                caught it before it shipped. `/board` is the Content Board
                view of the same feed, so it lights the same row. */}
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActiveRoute('/discover') || isActiveRoute('/board')}
                tooltip="Discover"
                className={SIDEBAR_ROW_HEIGHT}
              >
                <Link to="/discover" onClick={closeMobileSidebar}>
                  <BinocularsIcon weight="regular" />
                  <span>Discover</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* Workspace-gated: visible only in the two internal workspaces
                operating Reach, never as a disabled row elsewhere. */}
            {reachEnabled && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActiveRoute('/reach')}
                  tooltip="Reach"
                  className={SIDEBAR_ROW_HEIGHT}
                >
                  <Link to="/reach" onClick={closeMobileSidebar}>
                    <HandshakeIcon weight="regular" />
                    <span>Reach</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            {/* Hidden only for a classified non-commerce workspace with no
                catalog; the route stays reachable. See productsVisibility.ts. */}
            {productsVisible && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActiveRoute('/products')}
                  tooltip="Products"
                  className={SIDEBAR_ROW_HEIGHT}
                >
                  <Link to="/products" onClick={closeMobileSidebar}>
                    <PackageIcon weight="regular" />
                    <span>Products</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
            {/* Engagement (the comments inbox) is SUPPRESSED, not removed —
                founder, 2026-08-23: it reads as too much surface area for
                where the product is, and comes back if customers ask. The
                dock, its components and its data layer are all untouched;
                only this entry point is withheld. See
                `ENGAGEMENT_NAV_VISIBLE` in feature-flags.ts before deleting
                anything downstream of it. */}
            {ENGAGEMENT_NAV_VISIBLE && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={engagementOpen}
                  tooltip="Engagement"
                  className={SIDEBAR_ROW_HEIGHT}
                  onClick={() => {
                    openDock('engagement');
                    closeMobileSidebar();
                  }}
                >
                  <ChatCircleDotsIcon weight="regular" />
                  <span>Engagement</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </SidebarGroup>

        {/* Tools — the surfaces you go to in order to MAKE or operate something, as
            against the two above, which you go to in order to know something.
            The label was "Workspace" until 2026-08-07; that word now names the
            single settings destination at the bottom, matching the reference,
            so it could not keep doing double duty here.

            The More menu was deleted outright — it held exactly one
            item (Products), and a dropdown wrapping a single link is a click
            tax with no grouping value; Products now sits with the sync that
            produces it, in Settings → Integrations. Projects retired as a nav
            noun: a brief IS a `projects` row, so Creatives → Briefs is the way
            in. (The `/projects` LIST route deliberately survives — BriefsPage
            still hands off to it while the rename lands, so redirecting it
            would bounce the user straight back to the page they left.)
            Discover left this group for Core on 2026-08-10 — see the comment
            on its row above. */}
        <SidebarGroup className="pt-1">
          {/* "Create", not "Tools". A group label should name what its members
              have in common; "Tools" names nothing, and every row in this app
              is a tool. These five are the things you MAKE with. */}
          <SidebarGroupLabel className="h-7 group-data-[collapsible=icon]:hidden">
            Create
          </SidebarGroupLabel>
          <SidebarMenu className={SIDEBAR_NAV_MENU}>
            {/* Same plain line-icon treatment as the Core rows; colour is reserved
                for the active-row fill. */}
            {CREATE_NAV_ITEMS.map((item) => {
              const isActive = item.match.some((prefix) => location.pathname.startsWith(prefix));
              return (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    tooltip={item.label}
                    className={SIDEBAR_ROW_HEIGHT}
                  >
                    <Link to={item.href} onClick={closeMobileSidebar}>
                      <item.icon weight="regular" />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>

        {/* Always rendered (not gated on draftCount > 0) — DraftLibrary shows
            its own "No drafts yet" EmptyState internally. A vanishing section
            reads as a layout bug ("why did this just disappear?"); a visible
            header + honest empty state reads as "there's nothing here yet". */}
        <SidebarGroup className="pt-1 group-data-[collapsible=icon]:hidden">
          <Collapsible open={postDraftsOpen} onOpenChange={setPostDraftsOpen}>
            <SidebarSectionHeader
              label="Post drafts"
              open={postDraftsOpen}
              actionIcon={<NotePencilIcon />}
              actionLabel="New post"
              actionTooltip={isCreatingManualPost ? 'Creating post…' : 'New post'}
              actionDisabled={isCreatingManualPost}
              onAction={() => {
                void createManualPostDraft({ title: 'New draft' });
                closeMobileSidebar();
              }}
            />
            <CollapsibleContent>
              <DraftLibrary />
            </CollapsibleContent>
          </Collapsible>
        </SidebarGroup>

        {/* Recent chats — fills the previously-empty 400px in the mobile
            drawer with session deep-links. Linear's "Your views" recent
            rail. Hidden when collapsed-icon mode (desktop rail). Collapses
            to a scan-able 5 by default (sibling parity with Post drafts);
            "Show more" reveals the rest + arms IntersectionObserver
            pagination for continued scroll-loading, ChatGPT-style.
            Always rendered (not gated on recentSessions.length > 0) — an
            empty list shows "No chats yet" instead of the section silently
            vanishing, matching Post drafts' EmptyState treatment. */}
        {(() => {
          const visibleSessions = recentChatsExpanded
            ? recentSessions
            : recentSessions.slice(0, RECENT_CHATS_COLLAPSED);
          const hasMoreToShow =
            !recentChatsExpanded &&
            (recentSessions.length > RECENT_CHATS_COLLAPSED || hasMoreSessions);
          return (
            <SidebarGroup className="pt-1 group-data-[collapsible=icon]:hidden">
              <Collapsible open={recentChatsOpen} onOpenChange={setRecentChatsOpen}>
                {/* The + is positioned against THIS wrapper, not the SidebarGroup:
                    the group is already `relative` and wraps the whole collapsible
                    body, so absolute coordinates taken from it drift down the
                    section as the chat list grows. Scoping the containing block to
                    the header row keeps `top-1/2` meaning "centred on the label". */}
                {/* Starting a chat lives on the section header rather than as a
                    list row, the way ChatGPT's does — a "New chat" ROW would be
                    the eighth destination-shaped thing in a sidebar this pass is
                    shrinking. */}
                <SidebarSectionHeader
                  label="Chat"
                  open={recentChatsOpen}
                  actionIcon={<NotePencilIcon />}
                  actionLabel="New chat"
                  actionTooltip="New chat · ⌘0"
                  onAction={() => {
                    triggerNewChat();
                    closeMobileSidebar();
                  }}
                />
                <CollapsibleContent>
                  {!isLoadingRecentSessions && recentSessions.length === 0 ? (
                    <EmptyState variant="inline" title="No chats yet" />
                  ) : (
                    // Same 2px rhythm as every nav list. This was a bare
                    // `<SidebarMenu>`, which inherits the primitive's `gap-1`
                    // (4px) — so conversations sat at DOUBLE the spacing of the
                    // rows above them, which reads as an inconsistency rather
                    // than as a deliberate grouping. Same bug in `DraftLibrary`.
                    <SidebarMenu className={SIDEBAR_NAV_MENU}>
                      {visibleSessions.map((session) => (
                        <SidebarMenuItem key={session.id}>
                          <SidebarMenuButton
                            asChild
                            tooltip={session.title}
                            className={RECENT_TITLE_ROW}
                          >
                            <Link to={routes.resumeChat(session.id, session.workspaceId)}>
                              <span className="truncate">{session.title}</span>
                            </Link>
                          </SidebarMenuButton>
                          <ChatSessionMenu
                            sessionId={session.id}
                            title={session.title}
                            side="right"
                            align="start"
                          >
                            <SidebarMenuAction
                              showOnHover
                              aria-label="Conversation actions"
                              className="text-muted-foreground hover:text-foreground focus-visible:opacity-100"
                            >
                              <DotsThreeIcon weight="bold" />
                            </SidebarMenuAction>
                          </ChatSessionMenu>
                        </SidebarMenuItem>
                      ))}
                      {hasMoreToShow && (
                        <SidebarMenuItem>
                          <button
                            type="button"
                            onClick={() => setRecentChatsExpanded(true)}
                            className="w-full rounded-md px-2 py-1.5 text-left text-detail text-muted-foreground transition-colors hover:text-foreground"
                          >
                            Show more
                          </button>
                        </SidebarMenuItem>
                      )}
                      {recentChatsExpanded &&
                        isFetchingMoreSessions &&
                        Array.from({ length: 3 }).map((_, i) => (
                          <SidebarMenuItem key={`recent-skeleton-${i}`}>
                            <div className="flex items-center p-2">
                              <Skeleton className="h-3.5 w-[70%]" />
                            </div>
                          </SidebarMenuItem>
                        ))}
                    </SidebarMenu>
                  )}
                  {recentChatsExpanded && <div ref={recentSessionsSentinelRef} className="h-px" />}
                </CollapsibleContent>
              </Collapsible>
            </SidebarGroup>
          );
        })()}
      </SidebarContent>

      <SidebarFooter className="gap-0 p-2">
        {isTeamInvitesEnabled() && !isTeammate && !isPreviewAccount ? (
          <button
            type="button"
            onClick={() => openSettings('team')}
            className="group-data-[collapsible=icon]:hidden mb-2 hidden md:flex flex-col items-start gap-2 rounded-xl border border-border/60 bg-card p-3 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex size-8 items-center justify-center rounded-lg bg-muted">
              <HandsClappingIcon className="size-4 text-muted-foreground" weight="regular" />
            </span>
            <span className="text-sm font-normal text-foreground/85">Invite team members</span>
            <span className="text-detail text-muted-foreground">
              Bring your team in to collaborate and share your creations.
            </span>
          </button>
        ) : null}
        {/* Connected accounts used to have their own facepile row here —
            folded into the header brand switcher's "Connected accounts"
            disclosure (2026-08-18) so there's one identity entry point at
            the top instead of two. */}
        <SharedAccountHint />
        <SentryFeedbackTrigger />
        <div aria-hidden className="mx-1 mt-1 border-t border-border/40" />
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              aria-label={`${sidebarDisplayName}, open user menu`}
              className="mt-1 flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left hover:bg-sidebar-accent transition-colors data-[state=open]:bg-sidebar-accent group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:gap-0"
            >
              {/* No `tier` prop, deliberately. It stamped a filled star on the
                  corner of the user avatar; founder, 2026-08-12: "it looks
                  really funny". A plan badge is not identity — the avatar's
                  only job here is "this is you", and the plan is already stated
                  twice in the menu this button opens (the usage row and the
                  Upgrade CTA). `getAvatarTier` is still used for the composer's
                  tier mark, which is a different surface with a real reason.

                  The ring is now unconditional: it previously appeared only
                  when there was neither a photo nor a tier, so a photo avatar
                  had no edge at all and dissolved into the sidebar. */}
              <Avatar
                src={avatarUrl}
                name={user?.user_metadata?.full_name}
                email={user?.email}
                size="xs"
                className="shrink-0 ring-1 ring-border/60"
                fallbackClassName="bg-background"
              />
              <div className="flex min-w-0 flex-1 items-center group-data-[collapsible=icon]:hidden">
                <OverflowFadeText
                  className="block text-sm font-medium leading-tight text-sidebar-foreground"
                  title={sidebarDisplayName}
                >
                  {sidebarDisplayName}
                </OverflowFadeText>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="start"
            sideOffset={8}
            className="w-56 overflow-x-hidden overflow-y-auto overscroll-contain rounded-xl border border-border/60 bg-popover/95 p-0 shadow-elevation-3 backdrop-blur-xl"
          >
            <UserMenuHeader />

            <div className="p-1">
              {/* Brand kit switching/management moved to the header switcher
                  above (2026-08-18) — no longer duplicated here. */}
              <DropdownMenuItem
                aria-label={sidebarCreditsLabel}
                onClick={() => {
                  openSettings('usage');
                  setMenuOpen(false);
                }}
                className={USER_MENU_ITEM}
              >
                <ChartLineUpIcon className={USER_MENU_ICON} weight="regular" />
                <span className={USER_MENU_LABEL}>Credits left</span>
                <span className="ml-auto text-detail text-muted-foreground tabular-nums">
                  {wallet.isLoading
                    ? '—'
                    : wallet.data
                      ? wallet.data.creditsAvailable.toLocaleString()
                      : '—'}
                </span>
              </DropdownMenuItem>
              {/* Appearance sits directly under Credits left (founder,
                  2026-09-09: it "looks weird" next to Earn $50) — the two
                  rows that carry a trailing VALUE (a credit count, a theme
                  name) are grouped together; Connections/Settings,
                  which are plain action rows with no value, follow as their
                  own group. The caret stays: unlike Credits left (which
                  navigates to Settings on click), Appearance opens an inline
                  submenu, and the caret is the one signal that tells the
                  user this row expands rather than navigates.

                  Appearance is a SUBMENU, not an inline segmented toggle.
                  It carried a `SegmentedToggle` (icon variant: a 32px capsule
                  of three 28px buttons) inside a row whose siblings are a
                  14px icon beside a 12px label — a 44px row in a menu of 28px
                  rows, plus a separator above it to fence it off. Founder,
                  2026-08-23: _"way too tall, and it's creating weird
                  separation... we have way too many lines on that thing...
                  take away that divided line above appearance and just make
                  those all one thing"_.

                  A submenu makes the row structurally identical to every
                  other row here — icon, label, a quiet value on the right —
                  which is what "all one thing" means, and it is the shape
                  Notion and Linear both use for theme inside an account menu.
                  The separator is gone with it: with no oversized control to
                  fence, the rows are one uninterrupted group. */}
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  className={cn(USER_MENU_ITEM, 'data-[state=open]:bg-muted/50')}
                  caretClassName="ml-1 size-3 text-muted-foreground/70"
                >
                  <PaletteIcon className={USER_MENU_ICON} weight="regular" />
                  <span className={USER_MENU_LABEL}>Appearance</span>
                  <span className="ml-auto text-detail text-muted-foreground">
                    {activeAppearanceLabel}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="w-40 p-1">
                    {appearanceOptions.map((option) => {
                      const OptionIcon = option.icon;
                      return (
                        <DropdownMenuItem
                          key={option.value}
                          className={USER_MENU_ITEM}
                          onClick={() => void setAppearance(option.value as ThemeMode)}
                        >
                          <OptionIcon className={USER_MENU_ICON} weight="regular" />
                          <span className={USER_MENU_LABEL}>{option.label}</span>
                          {themeMode === option.value && (
                            <CheckIcon
                              className="ml-auto size-3.5 text-muted-foreground"
                              weight="bold"
                              aria-hidden
                            />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              {/* Re-added 2026-09-09 (founder request) after being dropped
                  2026-08-22 and again 2026-08-23 — see git history on this
                  line for that reasoning, which no longer applies as written:
                  it argued the row was a third door to a workspace switcher
                  disclosure that no longer exists on this menu. Uses the same
                  `PlugIcon` as Settings → Connections (`SettingsDialogProvider.tsx`),
                  so the glyph matches wherever "Connections" appears. */}
              <DropdownMenuItem
                onClick={() => {
                  openSettings('integrations');
                  setMenuOpen(false);
                }}
                className={USER_MENU_ITEM}
              >
                <PlugIcon className={USER_MENU_ICON} weight="regular" />
                <span className={USER_MENU_LABEL}>Connections</span>
                {connectionsToFix > 0 ? (
                  <span className="ml-auto text-detail text-warning">
                    {connectionsToFix} to fix
                  </span>
                ) : null}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  openSettings('profile');
                  setMenuOpen(false);
                }}
                className={USER_MENU_ITEM}
              >
                <GearIcon className={USER_MENU_ICON} weight="regular" />
                <span className={USER_MENU_LABEL}>Settings</span>
                <DropdownMenuShortcut className="text-detail tracking-normal">
                  {SETTINGS_SHORTCUT_HINT}
                </DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  navigate('/help');
                  setMenuOpen(false);
                  closeMobileSidebar();
                }}
                className={USER_MENU_ITEM}
              >
                <QuestionIcon className={USER_MENU_ICON} weight="regular" />
                <span className={USER_MENU_LABEL}>Help &amp; Support</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
                className={USER_MENU_ITEM}
              >
                <SignOutIcon className={USER_MENU_ICON} weight="regular" />
                <span className={USER_MENU_LABEL}>Sign out</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
      <AddBrandDialog
        open={addBrandOpen}
        onOpenChange={setAddBrandOpen}
        onStart={startBrandResearcherAgent}
        existingCount={brandKits.length}
        isLocation={tier === 'franchise' || tier === 'enterprise'}
      />
    </Sidebar>
  );
}
